import { CAREER_VERSION, type Career } from "./model.js";

/**
 * Donde vive la carrera en este navegador. Una sola a la vez: la carrera es
 * TU vida, no una partida guardada entre diez.
 *
 * Se guarda despues de cada paso (empezar partido, apuntarlo, fichar), no al
 * cerrar: cerrar la pestaña a mitad de partido tiene que encontrar el partido
 * empezado para darlo por jugado sin ti.
 */
export const CAREER_KEY = "fynta.career";
/**
 * Cuando se guardo por ultima vez. Va aparte de la carrera (que es logica pura
 * y no sabe de relojes) y decide, entre dos carreras DISTINTAS, cual es la que
 * el jugador tiene en la cabeza: la ultima que toco. Ver `cloud.ts`.
 */
export const CAREER_AT_KEY = "fynta.career.at";

/** Quien ha cambiado la carrera: el jugador aqui, o una copia que llego de la cuenta. */
export type CareerChangeSource = "local" | "cloud";
const listeners = new Set<(source: CareerChangeSource) => void>();

/** Avisa de cada guardado. Devuelve con que dejar de escuchar. */
export function onCareerChange(fn: (source: CareerChangeSource) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const notify = (source: CareerChangeSource): void => {
  for (const fn of listeners) fn(source);
};

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Lo minimo para no abrir una carrera rota: version y las piezas que se leen siempre. */
function looksLikeCareer(raw: unknown): raw is Career {
  if (!raw || typeof raw !== "object") return false;
  const c = raw as Partial<Career>;
  return (
    c.version === CAREER_VERSION &&
    typeof c.seed === "number" &&
    Boolean(c.player) &&
    Array.isArray(c.clubs) &&
    Boolean(c.season) &&
    Array.isArray(c.feed)
  );
}

export function loadCareer(storage: StorageLike | null = safeLocalStorage()): Career | null {
  try {
    const raw = storage?.getItem(CAREER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return looksLikeCareer(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveCareer(
  career: Career,
  storage: StorageLike | null = safeLocalStorage(),
  opts: { at?: number; source?: CareerChangeSource } = {},
): void {
  try {
    storage?.setItem(CAREER_KEY, JSON.stringify(career));
    storage?.setItem(CAREER_AT_KEY, String(opts.at ?? Date.now()));
  } catch {
    // Sin sitio: se juega igual, pero esta pestaña es todo lo que dura.
  }
  notify(opts.source ?? "local");
}

/** Cuando se guardo la carrera por ultima vez; 0 si nunca (o no se sabe). */
export function careerSavedAt(storage: StorageLike | null = safeLocalStorage()): number {
  try {
    const at = Number(storage?.getItem(CAREER_AT_KEY) ?? "");
    return Number.isFinite(at) && at > 0 ? at : 0;
  } catch {
    return 0;
  }
}

export function clearCareer(storage: StorageLike | null = safeLocalStorage()): void {
  try {
    storage?.removeItem(CAREER_KEY);
    storage?.removeItem(CAREER_AT_KEY);
  } catch {
    // Nada que borrar.
  }
}
